import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Tabs, Table, Button, Input, InputNumber, ColorPicker,
  Space, message, Popconfirm, Modal, Form, Tag, Typography, Select, Row, Col, Checkbox, Switch,
  Alert, Divider, Grid
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined,
  ReloadOutlined, SettingOutlined, UserSwitchOutlined, CrownOutlined,
  LockOutlined, EditOutlined, StopOutlined, UserOutlined, CheckCircleOutlined,
  UndoOutlined, CloudOutlined, SyncOutlined, GiftOutlined, HistoryOutlined
} from '@ant-design/icons';
import { ROLE_OPTIONS } from './constants';

const { Option } = Select;

const { Text } = Typography;

const API_BASE_URL = '';
const { useBreakpoint } = Grid;

const BOSS_TYPE_LABELS = {
  HT: 'Horntail (HT)',
  PB: 'Pink Bean (PB)'
};

// ==================== 掉落物品配置组件 ====================
const LootConfig = ({ onConfigChange }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addItemModal, setAddItemModal] = useState(false);
  const [addServiceModal, setAddServiceModal] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/loot-config`);
      if (res.ok) {
        setConfig(await res.json());
      }
    } catch (e) {
      messageApi.error('加载失败: ' + e.message);
    }
    setLoading(false);
  }, [messageApi]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/loot-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        messageApi.success('配置已保存');
        if (onConfigChange) onConfigChange();
      }
    } catch (e) {
      messageApi.error('保存失败: ' + e.message);
    }
    setSaving(false);
  };

  const resetConfig = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loot-config/reset`, { method: 'POST' });
      if (res.ok) {
        messageApi.success('已重置为默认值');
        loadConfig();
        if (onConfigChange) onConfigChange();
      }
    } catch (e) {
      messageApi.error('重置失败');
    }
  };

  const updateItem = (index, field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      newConfig.items = [...newConfig.items];
      newConfig.items[index] = { ...newConfig.items[index], [field]: value };
      return newConfig;
    });
  };

  const deleteItem = (index) => {
    setConfig(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const addItem = (values) => {
    setConfig(prev => ({
      ...prev,
      items: [...prev.items, { 
        name: values.name, 
        mesoValue: values.mesoValue || 0, 
        coinValue: values.coinValue || 0,
        bossType: values.bossType || 'HT'
      }]
    }));
    setAddItemModal(false);
    form.resetFields();
  };

  const deleteService = (index) => {
    setConfig(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.filter((_, i) => i !== index)
    }));
  };

  const addService = (values) => {
    setConfig(prev => ({
      ...prev,
      serviceTypes: [...(prev.serviceTypes || []), { name: values.name, description: values.description || '' }]
    }));
    setAddServiceModal(false);
    form.resetFields();
  };

  const itemColumns = [
    { title: '物品名称', dataIndex: 'name', render: (text, _, index) => (
      <Input value={text} onChange={e => updateItem(index, 'name', e.target.value)} style={{ width: 180 }} />
    )},
    { title: 'Boss 类型', dataIndex: 'bossType', width: 120, render: (text, _, index) => (
      <select value={text || 'HT'} onChange={e => updateItem(index, 'bossType', e.target.value)} style={{ width: 100, padding: '4px 8px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
        <option value="HT">HT</option>
        <option value="NT">NT</option>
        <option value="PB">PB</option>
      </select>
    )},
    { title: 'Meso 价值', dataIndex: 'mesoValue', render: (value, _, index) => (
      <InputNumber value={value} onChange={v => updateItem(index, 'mesoValue', v || 0)} min={0} style={{ width: 130 }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={v => v.replace(/,/g, '')} />
    )},
    { title: 'Coin 价值', dataIndex: 'coinValue', render: (value, _, index) => (
      <InputNumber value={value} onChange={v => updateItem(index, 'coinValue', v || 0)} min={0} style={{ width: 100 }} />
    )},
    { title: '操作', width: 60, render: (_, __, index) => (
      <Popconfirm title="确定删除？" onConfirm={() => deleteItem(index)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
    )}
  ];

  const serviceColumns = [
    { title: '服务名称', dataIndex: 'name', render: (text, _, index) => (
      <Input value={text} onChange={e => {
        setConfig(prev => {
          const newConfig = { ...prev };
          newConfig.serviceTypes = [...newConfig.serviceTypes];
          newConfig.serviceTypes[index] = { ...newConfig.serviceTypes[index], name: e.target.value };
          return newConfig;
        });
      }} style={{ width: 180 }} />
    )},
    { title: '描述', dataIndex: 'description', render: (text, _, index) => (
      <Input value={text} onChange={e => {
        setConfig(prev => {
          const newConfig = { ...prev };
          newConfig.serviceTypes = [...newConfig.serviceTypes];
          newConfig.serviceTypes[index] = { ...newConfig.serviceTypes[index], description: e.target.value };
          return newConfig;
        });
      }} style={{ width: 200 }} />
    )},
    { title: '操作', width: 60, render: (_, __, index) => (
      <Popconfirm title="确定删除？" onConfirm={() => deleteService(index)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
    )}
  ];

  if (!config) return <Card loading={loading} />;

  return (
    <div style={{ padding: '24px', maxWidth: 1000, margin: '0 auto' }}>
      {contextHolder}
      <Card 
        title={<Space><SettingOutlined /><span>掉落物品与服务配置</span></Space>}
        extra={
          <Space>
            <Popconfirm 
              title="确定重置为默认值？" 
              description="此操作将覆盖所有已配置的物品、服务和角色定位分成设置，无法恢复！"
              onConfirm={resetConfig}
              okText="确认重置"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button icon={<ReloadOutlined />} danger>重置默认</Button>
            </Popconfirm>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveConfig}>保存配置</Button>
          </Space>
        }
        loading={loading}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>配置 Boss 掉落物品的价值（Meso/Coin）和出售服务类型，用于计算历史记录的总收入。</Text>
        
        {/* 掉落物品配置 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong style={{ fontSize: 16 }}>掉落物品价值</Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setAddItemModal(true); }}>添加物品</Button>
          </div>
          <Table columns={itemColumns} dataSource={config.items?.map((item, i) => ({ ...item, key: i }))} pagination={false} size="small" bordered />
        </div>

        {/* 服务类型配置 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong style={{ fontSize: 16 }}>出售服务类型</Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setAddServiceModal(true); }}>添加服务</Button>
          </div>
          <Table columns={serviceColumns} dataSource={config.serviceTypes?.map((s, i) => ({ ...s, key: i }))} pagination={false} size="small" bordered />
        </div>

        {/* 角色定位分成配置 - 按 Boss 类型分 Tab */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong style={{ fontSize: 16 }}>角色定位额外分成</Text>
            <Text type="secondary">根据 Boss 类型和角色定位设置额外的分成比例</Text>
          </div>
          <Tabs
            type="card"
            items={['HT', 'PB'].map(bossType => ({
              key: bossType,
              label: bossType === 'HT' ? 'Horntail (HT)' : 'Pink Bean (PB)',
              children: (
                <Table 
                  columns={[
                    { title: '角色定位', dataIndex: 'label', width: 180 },
                    { 
                      title: '额外分成比例', 
                      dataIndex: 'bonusPercent', 
                      render: (value, _, index) => (
                        <Space>
                          <InputNumber
                            min={-100}
                            max={100}
                            value={value}
                            onChange={v => {
                              setConfig(prev => {
                                const newConfig = { ...prev };
                                // 确保 roleBonus 是按 Boss 类型组织的对象
                                if (!newConfig.roleBonus || Array.isArray(newConfig.roleBonus)) {
                                  // 兼容旧格式：转换为新格式
                                  const oldData = newConfig.roleBonus || [];
                                  newConfig.roleBonus = {
                                    HT: JSON.parse(JSON.stringify(oldData)),
                                    NT: JSON.parse(JSON.stringify(oldData)),
                                    PB: JSON.parse(JSON.stringify(oldData))
                                  };
                                }
                                newConfig.roleBonus = { ...newConfig.roleBonus };
                                newConfig.roleBonus[bossType] = [...(newConfig.roleBonus[bossType] || [])];
                                newConfig.roleBonus[bossType][index] = { 
                                  ...newConfig.roleBonus[bossType][index], 
                                  bonusPercent: v || 0 
                                };
                                return newConfig;
                              });
                            }}
                            style={{ width: 80 }}
                            addonAfter="%"
                          />
                          <Tag color={value > 0 ? 'green' : value < 0 ? 'red' : 'default'}>{value > 0 ? '+' : ''}{value || 0}%</Tag>
                        </Space>
                      )
                    },
                    { 
                      title: '说明', 
                      render: (_, record) => (
                        <Text type="secondary">
                          {record.bonusPercent > 0 
                            ? `该角色在 ${bossType} 可获得 ${record.bonusPercent}% 额外奖励`
                            : record.bonusPercent < 0
                            ? `该角色在 ${bossType} 将减少 ${Math.abs(record.bonusPercent)}% 分成`
                            : '无额外奖励'}
                        </Text>
                      )
                    }
                  ]}
                  dataSource={(() => {
                    // 兼容旧格式和新格式
                    const roleBonus = config.roleBonus;
                    if (Array.isArray(roleBonus)) {
                      return roleBonus.map((r, i) => ({ ...r, key: i }));
                    }
                    return (roleBonus?.[bossType] || []).map((r, i) => ({ ...r, key: i }));
                  })()}
                  pagination={false}
                  size="small"
                  bordered
                />
              )
            }))}
          />
        </div>
      </Card>

      {/* 添加物品 Modal */}
      <Modal title="添加掉落物品" open={addItemModal} onCancel={() => setAddItemModal(false)} footer={null}>
        <Form form={form} layout="vertical" onFinish={addItem}>
          <Form.Item name="name" label="物品名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="bossType" label="Boss 类型" initialValue="HT">
            <select style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
              <option value="HT">HT</option><option value="NT">NT</option><option value="PB">PB</option>
            </select>
          </Form.Item>
          <Form.Item name="mesoValue" label="Meso 价值"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="coinValue" label="Coin 价值"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item><Space style={{ width: '100%', justifyContent: 'flex-end' }}><Button onClick={() => setAddItemModal(false)}>取消</Button><Button type="primary" htmlType="submit">添加</Button></Space></Form.Item>
        </Form>
      </Modal>

      {/* 添加服务 Modal */}
      <Modal title="添加服务类型" open={addServiceModal} onCancel={() => setAddServiceModal(false)} footer={null}>
        <Form form={form} layout="vertical" onFinish={addService}>
          <Form.Item name="name" label="服务名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input /></Form.Item>
          <Form.Item><Space style={{ width: '100%', justifyContent: 'flex-end' }}><Button onClick={() => setAddServiceModal(false)}>取消</Button><Button type="primary" htmlType="submit">添加</Button></Space></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ==================== Boss 状态配置组件 ====================
const BossConfig = ({ onConfigChange }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('HT');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // 加载配置
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else {
        messageApi.error('加载配置失败');
      }
    } catch (e) {
      messageApi.error('加载配置失败: ' + e.message);
    }
    setLoading(false);
  }, [messageApi]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // 保存配置
  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        messageApi.success('配置已保存');
        // 通知父组件配置已更新
        if (onConfigChange) {
          onConfigChange();
        }
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败: ' + e.message);
    }
    setSaving(false);
  };

  // 重置配置
  const resetConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-config/reset`, {
        method: 'POST'
      });
      if (res.ok) {
        messageApi.success('配置已重置为默认值');
        loadConfig();
        // 通知父组件配置已更新
        if (onConfigChange) {
          onConfigChange();
        }
      } else {
        messageApi.error('重置失败');
      }
    } catch (e) {
      messageApi.error('重置失败: ' + e.message);
    }
    setLoading(false);
  };

  // 更新状态
  const updateStatus = (bossType, index, field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      newConfig[bossType] = { ...newConfig[bossType] };
      newConfig[bossType].statuses = [...newConfig[bossType].statuses];
      newConfig[bossType].statuses[index] = {
        ...newConfig[bossType].statuses[index],
        [field]: value
      };
      return newConfig;
    });
  };

  // 删除状态
  const deleteStatus = (bossType, index) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      newConfig[bossType] = { ...newConfig[bossType] };
      newConfig[bossType].statuses = newConfig[bossType].statuses.filter((_, i) => i !== index);
      return newConfig;
    });
    messageApi.success('状态已删除');
  };

  // 添加状态
  const addStatus = (values) => {
    const { name, ratio, color } = values;
    const colorHex = typeof color === 'string' ? color : color.toHexString();
    
    setConfig(prev => {
      const newConfig = { ...prev };
      newConfig[activeTab] = { ...newConfig[activeTab] };
      newConfig[activeTab].statuses = [
        ...newConfig[activeTab].statuses,
        { name, ratio: ratio / 100, color: colorHex }
      ];
      return newConfig;
    });
    
    setAddModalVisible(false);
    form.resetFields();
    messageApi.success('状态已添加');
  };

  // 移动状态顺序
  const moveStatus = (bossType, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= config[bossType].statuses.length) return;
    
    setConfig(prev => {
      const newConfig = { ...prev };
      newConfig[bossType] = { ...newConfig[bossType] };
      const statuses = [...newConfig[bossType].statuses];
      [statuses[index], statuses[newIndex]] = [statuses[newIndex], statuses[index]];
      newConfig[bossType].statuses = statuses;
      return newConfig;
    });
  };

  // 表格列配置
  const getColumns = (bossType) => [
    {
      title: '排序',
      width: 80,
      render: (_, record, index) => (
        <Space size="small">
          <Button 
            size="small" 
            disabled={index === 0}
            onClick={() => moveStatus(bossType, index, -1)}
          >
            ↑
          </Button>
          <Button 
            size="small" 
            disabled={index === config[bossType].statuses.length - 1}
            onClick={() => moveStatus(bossType, index, 1)}
          >
            ↓
          </Button>
        </Space>
      )
    },
    {
      title: '状态名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={e => updateStatus(bossType, index, 'name', e.target.value)}
          style={{ width: 150 }}
        />
      )
    },
    {
      title: '分配比例',
      dataIndex: 'ratio',
      key: 'ratio',
      render: (value, record, index) => (
        <Space>
          <InputNumber
            min={0}
            max={100}
            value={Math.round(value * 100)}
            onChange={v => updateStatus(bossType, index, 'ratio', (v || 0) / 100)}
            style={{ width: 80 }}
            addonAfter="%"
          />
          <Tag color={value === 0 ? 'default' : value === 1 ? 'success' : 'processing'}>
            {(value * 100).toFixed(0)}%
          </Tag>
        </Space>
      )
    },
    {
      title: '颜色',
      dataIndex: 'color',
      key: 'color',
      render: (color, record, index) => (
        <Space>
          <ColorPicker
            value={color}
            onChange={(c) => updateStatus(bossType, index, 'color', c.toHexString())}
          />
          <Tag color={color} style={{ color: '#fff', textShadow: '0 0 2px rgba(0,0,0,0.5)' }}>
            {record.name}
          </Tag>
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record, index) => (
        <Popconfirm
          title="确定删除此状态？"
          onConfirm={() => deleteStatus(bossType, index)}
          okText="删除"
          cancelText="取消"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ];

  // 渲染 Boss 配置 Tab
  const renderBossTab = (bossType) => {
    if (!config || !config[bossType]) return null;
    
    const statuses = config[bossType].statuses || [];
    
    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Text type="secondary">
              共 {statuses.length} 个状态
            </Text>
          </Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({ ratio: 100, color: '#52c41a' });
              setAddModalVisible(true);
            }}
          >
            添加状态
          </Button>
        </div>
        
        <Table
          columns={getColumns(bossType)}
          dataSource={statuses.map((s, i) => ({ ...s, key: i }))}
          pagination={false}
          size="middle"
          bordered
        />
        
        {/* 状态预览 */}
        <Card size="small" title="状态预览" style={{ marginTop: 16 }}>
          <Space wrap>
            {statuses.map((status, index) => (
              <Tag 
                key={index} 
                color={status.color}
                style={{ 
                  color: '#fff', 
                  textShadow: '0 0 2px rgba(0,0,0,0.5)',
                  padding: '4px 12px',
                  fontSize: 14
                }}
              >
                {status.name} ({(status.ratio * 100).toFixed(0)}%)
              </Tag>
            ))}
          </Space>
        </Card>
      </div>
    );
  };

  const tabItems = [
    { key: 'HT', label: BOSS_TYPE_LABELS.HT, children: renderBossTab('HT') },
    { key: 'PB', label: BOSS_TYPE_LABELS.PB, children: renderBossTab('PB') },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: 1000, margin: '0 auto' }}>
      {contextHolder}
      
      <Card 
        title={
          <Space>
            <SettingOutlined />
            <span>Boss 运行状态配置</span>
          </Space>
        }
        extra={
          <Space>
            <Popconfirm
              title="确定重置所有配置为默认值？"
              description="此操作将覆盖所有 Boss 运行状态配置，无法恢复！"
              onConfirm={resetConfig}
              okText="确认重置"
              okButtonProps={{ danger: true }}
              cancelText="取消"
            >
              <Button icon={<ReloadOutlined />}>
                重置默认
              </Button>
            </Popconfirm>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              loading={saving}
              onClick={saveConfig}
            >
              保存配置
            </Button>
          </Space>
        }
        loading={loading}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            配置各 Boss 类型的运行状态及其对应的分配比例。分配比例用于计算角色在该状态下可获得的奖励比例。
          </Text>
        </div>
        
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
        />
      </Card>
      
      {/* 添加状态 Modal */}
      <Modal
        title={`添加 ${BOSS_TYPE_LABELS[activeTab]} 状态`}
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={addStatus}
          initialValues={{ ratio: 100, color: '#52c41a' }}
        >
          <Form.Item
            name="name"
            label="状态名称"
            rules={[{ required: true, message: '请输入状态名称' }]}
          >
            <Input placeholder="例如：AllDone, Legs, etc." />
          </Form.Item>
          
          <Form.Item
            name="ratio"
            label="分配比例 (%)"
            rules={[{ required: true, message: '请输入分配比例' }]}
          >
            <InputNumber 
              min={0} 
              max={100} 
              style={{ width: '100%' }} 
              addonAfter="%"
            />
          </Form.Item>
          
          <Form.Item
            name="color"
            label="状态颜色"
          >
            <ColorPicker format="hex" />
          </Form.Item>
          
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setAddModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">添加</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ==================== 管理员配置组件 ====================
const AdminConfig = ({ currentUser }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newAdmin, setNewAdmin] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [superAdmin, setSuperAdmin] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  
  // PIN 修改相关状态
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [pinTargetUser, setPinTargetUser] = useState('');
  const [newPinValue, setNewPinValue] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  
  // 是否是超级管理员
  const isSuperAdmin = currentUser?.username === superAdmin;

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users`);
      if (res.ok) {
        const data = await res.json();
        setAdmins(data.admins || []);
        setSuperAdmin(data.superAdmin || (data.admins && data.admins[0]) || '');
        setIsAdmin(data.admins?.includes(currentUser?.username) || false);
      }
    } catch (e) {
      messageApi.error('加载失败: ' + e.message);
    }
    setLoading(false);
  }, [messageApi, currentUser?.username]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);
  
  // 打开 PIN 修改弹窗
  const openPinModal = (targetUsername) => {
    setPinTargetUser(targetUsername);
    setNewPinValue('');
    setPinModalVisible(true);
  };
  
  // 修改 PIN 码
  const handleUpdatePin = async () => {
    if (!newPinValue || newPinValue.length !== 4 || !/^\d{4}$/.test(newPinValue)) {
      messageApi.warning('PIN 码必须是 4 位数字');
      return;
    }
    
    setPinLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/pin?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetUsername: pinTargetUser,
          newPin: newPinValue
        })
      });
      
      if (res.ok) {
        messageApi.success(`${pinTargetUser} 的 PIN 码已更新`);
        setPinModalVisible(false);
        setNewPinValue('');
        setPinTargetUser('');
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '修改失败');
      }
    } catch (e) {
      messageApi.error('修改失败: ' + e.message);
    }
    setPinLoading(false);
  };
  
  // 判断当前用户是否可以修改目标用户的 PIN
  const canEditPin = (targetUsername) => {
    // 超级管理员可以修改所有人的 PIN
    if (isSuperAdmin) return true;
    // 其他管理员只能修改自己的 PIN
    return currentUser?.username === targetUsername;
  };

  const saveAdmins = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admins })
      });
      if (res.ok) {
        messageApi.success('管理员列表已保存');
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败: ' + e.message);
    }
    setSaving(false);
  };

  const addAdmin = () => {
    const trimmed = newAdmin.trim();
    if (!trimmed) {
      messageApi.warning('请输入用户名');
      return;
    }
    if (admins.includes(trimmed)) {
      messageApi.warning('该用户已是管理员');
      return;
    }
    setAdmins([...admins, trimmed]);
    setNewAdmin('');
  };

  const removeAdmin = (username) => {
    if (username === currentUser?.username) {
      messageApi.warning('不能移除自己');
      return;
    }
    setAdmins(admins.filter(a => a !== username));
  };

  if (!isAdmin && !loading) {
    return (
      <div style={{ padding: '24px', maxWidth: 800, margin: '0 auto' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CrownOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <Typography.Title level={4} type="secondary">权限不足</Typography.Title>
            <Typography.Text type="secondary">只有管理员可以查看和修改此配置</Typography.Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 800, margin: '0 auto' }}>
      {contextHolder}
      <Card 
        title={<Space><UserSwitchOutlined /><span>管理员配置</span></Space>}
        extra={
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveAdmins}>
            保存配置
          </Button>
        }
        loading={loading}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          管理员拥有以下权限：审核收益分配、修改管理员列表。请谨慎添加管理员。
        </Text>

        <div style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input 
              placeholder="输入要添加的用户名" 
              value={newAdmin}
              onChange={e => setNewAdmin(e.target.value)}
              onPressEnter={addAdmin}
              style={{ maxWidth: 300 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={addAdmin}>
              添加管理员
            </Button>
          </Space.Compact>
        </div>

        <Table
          dataSource={admins.map((a, i) => ({ key: i, username: a }))}
          pagination={false}
          size="small"
          columns={[
            { 
              title: '用户名', 
              dataIndex: 'username', 
              render: (text) => (
                <Space>
                  <CrownOutlined style={{ color: '#faad14' }} />
                  <span>{text}</span>
                  {text === superAdmin && <Tag color="gold">超级管理员</Tag>}
                  {text === currentUser?.username && <Tag color="blue">当前用户</Tag>}
                </Space>
              )
            },
            {
              title: 'PIN 码',
              width: 120,
              render: (_, record) => (
                canEditPin(record.username) ? (
                  <Button 
                    type="link" 
                    size="small"
                    icon={<LockOutlined />}
                    onClick={() => openPinModal(record.username)}
                  >
                    修改 PIN
                  </Button>
                ) : (
                  <Text type="secondary">****</Text>
                )
              )
            },
            {
              title: '操作',
              width: 100,
              render: (_, record) => (
                <Popconfirm
                  title="确定移除此管理员？"
                  onConfirm={() => removeAdmin(record.username)}
                  disabled={record.username === currentUser?.username || record.username === superAdmin}
                >
                  <Button 
                    type="text" 
                    danger 
                    icon={<DeleteOutlined />}
                    disabled={record.username === currentUser?.username || record.username === superAdmin}
                  />
                </Popconfirm>
              )
            }
          ]}
          locale={{ emptyText: '暂无管理员' }}
        />
      </Card>
      
      {/* PIN 修改弹窗 */}
      <Modal
        title={<><LockOutlined /> 修改 PIN 码</>}
        open={pinModalVisible}
        onCancel={() => { setPinModalVisible(false); setNewPinValue(''); }}
        onOk={handleUpdatePin}
        confirmLoading={pinLoading}
        okText="保存"
        cancelText="取消"
        width={360}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>修改 <Text strong>{pinTargetUser}</Text> 的 PIN 码</Text>
        </div>
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="输入新的 4 位 PIN 码"
          maxLength={4}
          value={newPinValue}
          onChange={e => {
            const val = e.target.value.replace(/\D/g, '');
            setNewPinValue(val);
          }}
          onPressEnter={handleUpdatePin}
          style={{ letterSpacing: 8, textAlign: 'center', fontSize: 18 }}
        />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>PIN 码必须是 4 位数字</Text>
        </div>
      </Modal>
    </div>
  );
};

// 角色定位选项 已迁移至 constants.js（保持单一数据源）

// ==================== 用户管理组件 ====================
const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [messageApi, contextHolder] = message.useMessage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 并发加载用户、黑名单、管理员
      const [usersRes, blacklistRes, adminsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/users`),
        fetch(`${API_BASE_URL}/api/admin/blacklist?username=${encodeURIComponent(currentUser?.username)}`),
        fetch(`${API_BASE_URL}/api/admin/users`),
      ]);
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      if (blacklistRes.ok) {
        const data = await blacklistRes.json();
        setBlacklist(data.blacklist || []);
      }
      if (adminsRes.ok) {
        const data = await adminsRes.json();
        setAdmins(data.admins || []);
      }
    } catch (e) {
      messageApi.error('加载失败: ' + e.message);
    }
    setLoading(false);
  }, [messageApi, currentUser?.username]);

  useEffect(() => { loadData(); }, [loadData]);

  // 拉黑用户
  const handleBlacklist = async (targetUsername) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/blacklist?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUsername })
      });
      if (res.ok) {
        const data = await res.json();
        setBlacklist(data.blacklist || []);
        messageApi.success(`已将 ${targetUsername} 加入黑名单`);
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '操作失败');
      }
    } catch (e) {
      messageApi.error('操作失败: ' + e.message);
    }
  };

  // 解除拉黑
  const handleUnblacklist = async (targetUsername) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/blacklist/${encodeURIComponent(targetUsername)}?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        const data = await res.json();
        setBlacklist(data.blacklist || []);
        messageApi.success(`已将 ${targetUsername} 移出黑名单`);
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '操作失败');
      }
    } catch (e) {
      messageApi.error('操作失败: ' + e.message);
    }
  };

  const isBlacklisted = (username) => blacklist.some(b => b.toLowerCase() === username.toLowerCase());
  const isAdminUser = (username) => admins.includes(username);

  // 筛选后的用户列表
  const filteredUsers = users.filter(u => {
    if (!searchText) return true;
    return u.username.toLowerCase().includes(searchText.toLowerCase());
  });

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (text) => (
        <Space>
          <UserOutlined />
          <Text strong>{text}</Text>
          {isAdminUser(text) && <Tag color="gold" icon={<CrownOutlined />}>管理员</Tag>}
        </Space>
      )
    },
    {
      title: '关联角色数',
      dataIndex: 'characterCount',
      key: 'characterCount',
      width: 110,
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: '关联角色',
      dataIndex: 'characters',
      key: 'characters',
      render: (chars) => (
        <Space wrap size={[4, 4]}>
          {(chars || []).map(c => <Tag key={c}>{c}</Tag>)}
        </Space>
      )
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (t) => t ? new Date(t).toLocaleString() : '-'
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => (
        isBlacklisted(record.username) ? (
          <Tag color="red" icon={<StopOutlined />}>已禁用</Tag>
        ) : (
          <Tag color="green" icon={<CheckCircleOutlined />}>正常</Tag>
        )
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_, record) => {
        // 不能对管理员或自己操作
        if (isAdminUser(record.username)) {
          return <Text type="secondary">-</Text>;
        }
        if (record.username === currentUser?.username) {
          return <Text type="secondary">-</Text>;
        }
        if (isBlacklisted(record.username)) {
          return (
            <Popconfirm
              title="确定解除拉黑该用户？"
              description="解除后该用户可以重新登录系统"
              onConfirm={() => handleUnblacklist(record.username)}
              okText="确定"
              cancelText="取消"
            >
              <Button size="small" type="primary">
                解除拉黑
              </Button>
            </Popconfirm>
          );
        }
        return (
          <Popconfirm
            title="确定拉黑该用户？"
            description="拉黑后该用户将无法登录系统"
            onConfirm={() => handleBlacklist(record.username)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<StopOutlined />}>
              拉黑
            </Button>
          </Popconfirm>
        );
      }
    }
  ];

  return (
    <Card>
      {contextHolder}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space>
          <Text strong style={{ fontSize: 16 }}>
            <UserOutlined /> 用户管理
          </Text>
          <Tag color="blue">总用户: {users.length}</Tag>
          <Tag color="red">已禁用: {blacklist.length}</Tag>
        </Space>
        <Space>
          <Input.Search
            placeholder="搜索用户名"
            allowClear
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 220 }}
          />
          <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredUsers}
        rowKey="username"
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        rowClassName={(record) => isBlacklisted(record.username) ? 'blacklisted-row' : ''}
      />
    </Card>
  );
};

// ==================== 历史记录编辑弹窗 ====================
const HistoryEditModal = ({ visible, onCancel, onSuccess, historyData, currentUser }) => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingChar, setEditingChar] = useState(null);
  const [charForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (visible && historyData) {
      setCharacters(historyData.characters || []);
    }
  }, [visible, historyData]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${historyData.runId}/characters?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characters })
      });
      if (res.ok) {
        messageApi.success('角色信息已保存');
        onSuccess();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败');
    }
    setLoading(false);
  };

  const handleEditChar = (char) => {
    setEditingChar(char);
    charForm.setFieldsValue({
      name: char.name,
      team: char.team || 1,
      isLeader: char.isLeader || false,
      roles: char.roles || [],
      round1: char.round1 !== false,
      round2: char.round2 || false,
    });
  };

  const handleSaveChar = async () => {
    try {
      const values = await charForm.validateFields();
      setCharacters(prev => prev.map(c => 
        c.id === editingChar.id 
          ? { ...c, ...values }
          : c
      ));
      setEditingChar(null);
      charForm.resetFields();
    } catch (e) {
      // validation error
    }
  };

  const isPB = historyData?.bossType === 'PB';

  const charColumns = [
    { 
      title: '角色名', 
      dataIndex: 'name', 
      key: 'name',
      render: (name) => <Text strong>{name}</Text>
    },
    { 
      title: '队伍', 
      dataIndex: 'team', 
      key: 'team',
      width: 60,
      render: (team) => `队伍 ${team || 1}`
    },
    { 
      title: '角色定位', 
      dataIndex: 'roles', 
      key: 'roles',
      render: (roles) => (
        <Space size={2} wrap>
          {(roles || []).map(r => {
            const opt = ROLE_OPTIONS.find(o => o.value === r);
            return <Tag key={r} color={opt?.color || 'default'} style={{ fontSize: 10 }}>{opt?.label || r}</Tag>;
          })}
        </Space>
      )
    },
    { 
      title: 'Round', 
      key: 'rounds',
      width: 100,
      render: (_, record) => (
        <Space size={2}>
          {record.round1 && <Tag color="blue">R1</Tag>}
          {record.round2 && <Tag color="green">R2</Tag>}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 60,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditChar(record)}>
          编辑
        </Button>
      )
    }
  ];

  return (
    <Modal
      title={<><EditOutlined /> 编辑历史记录 - {historyData?.runName}</>}
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={loading}
      okText="保存修改"
      cancelText="取消"
      width={800}
    >
      {contextHolder}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Tag color={historyData?.bossType === 'HT' ? 'orange' : historyData?.bossType === 'PB' ? 'magenta' : 'green'}>
            {historyData?.bossType}
          </Tag>
          <Text type="secondary">完成时间: {historyData?.completedAt ? new Date(historyData.completedAt).toLocaleString() : '-'}</Text>
        </Space>
      </div>
      
      <Table
        dataSource={characters}
        columns={charColumns}
        rowKey="id"
        size="small"
        pagination={false}
        style={{ marginBottom: 16 }}
      />

      {/* 编辑角色弹窗 */}
      <Modal
        title={`编辑角色 - ${editingChar?.name}`}
        open={!!editingChar}
        onCancel={() => { setEditingChar(null); charForm.resetFields(); }}
        onOk={handleSaveChar}
        okText="确定"
        cancelText="取消"
        width={500}
      >
        <Form form={charForm} layout="vertical">
          <Form.Item name="name" label="角色名">
            <Input disabled />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="team" label="队伍">
                <Select>
                  {[1, 2, 3, 4, 5].map(n => (
                    <Option key={n} value={n}>队伍 {n}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isLeader" label="队长" valuePropName="checked">
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="roles" label="角色定位">
            <Select mode="multiple" placeholder="选择角色定位">
              {ROLE_OPTIONS.map(o => (
                <Option key={o.value} value={o.value}>
                  <Tag color={o.color}>{o.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="参与 Round">
            <Space>
              <Form.Item name="round1" valuePropName="checked" noStyle>
                <Checkbox>Round 1</Checkbox>
              </Form.Item>
              <Form.Item name="round2" valuePropName="checked" noStyle>
                <Checkbox disabled={isPB}>Round 2 {isPB && '(PB 只有 1 Round)'}</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

// ==================== 历史记录管理组件 ====================
const HistoryManagement = ({ currentUser }) => {
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingHistory, setEditingHistory] = useState(null);
  
  const pageSize = 20;
  const maxRecords = 100;

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/history?limit=${maxRecords}`);
      if (res.ok) {
        const data = await res.json();
        setHistoryList(data);
      }
    } catch (e) {
      messageApi.error('加载历史记录失败');
    }
    setLoading(false);
  }, [messageApi]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleDelete = async (runId, runName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${runId}?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        messageApi.success(`历史记录 "${runName}" 已删除`);
        loadHistory();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '删除失败');
      }
    } catch (e) {
      messageApi.error('删除失败');
    }
  };

  // 回滚已分配的 Run
  const handleRollback = async (runId, runName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/rollback-distribution/${runId}?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        messageApi.success(data.message || '回滚成功');
        loadHistory();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '回滚失败');
      }
    } catch (e) {
      messageApi.error('回滚失败: ' + e.message);
    }
  };

  const handleEdit = async (record) => {
    // 加载详细的历史记录数据
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${record.runId}`);
      if (res.ok) {
        const data = await res.json();
        setEditingHistory(data);
        setEditModalVisible(true);
      } else {
        messageApi.error('加载历史记录详情失败');
      }
    } catch (e) {
      messageApi.error('加载历史记录详情失败');
    }
  };

  const columns = [
    { 
      title: 'Run 名称', 
      dataIndex: 'runName', 
      key: 'runName',
      ellipsis: true,
      render: (name, record) => (
        <Space>
          <Tag color={record.bossType === 'HT' ? 'orange' : record.bossType === 'PB' ? 'magenta' : 'green'}>
            {record.bossType}
          </Tag>
          <Text strong>{name}</Text>
        </Space>
      )
    },
    { 
      title: '完成时间', 
      dataIndex: 'completedAt', 
      key: 'completedAt',
      width: 160,
      render: (t) => t ? new Date(t).toLocaleString() : '-'
    },
    {
      title: '分配状态',
      dataIndex: 'distributed',
      key: 'distributed',
      width: 100,
      render: (distributed) => distributed 
        ? <Tag color="success">已分配</Tag>
        : <Tag color="warning">未分配</Tag>
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      render: (_, record) => (
        <Space size="small">
          {!record.distributed && (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              编辑
            </Button>
          )}
          {record.distributed && (
            <Popconfirm
              title="确定回滚此分配的 Run？"
              description="将撤销所有账户的收益划入，恢复到未分配状态。此操作不可逆！"
              onConfirm={() => handleRollback(record.runId, record.runName)}
              okText="确认回滚"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" icon={<UndoOutlined />} style={{ color: '#fa8c16' }}>
                回滚
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="确定删除此历史记录？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record.runId, record.runName)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      {contextHolder}
      <Card 
        title={<><SettingOutlined /> 历史运行记录管理</>}
        extra={
          <Space>
            <Text type="secondary">显示最近 {maxRecords} 条记录</Text>
            <Button icon={<ReloadOutlined />} onClick={loadHistory}>刷新</Button>
          </Space>
        }
      >
        <Table
          dataSource={historyList}
          columns={columns}
          rowKey="runId"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: historyList.length,
            onChange: setCurrentPage,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          size="small"
          locale={{ emptyText: '暂无历史记录' }}
        />
      </Card>
      
      <HistoryEditModal
        visible={editModalVisible}
        onCancel={() => { setEditModalVisible(false); setEditingHistory(null); }}
        onSuccess={() => { setEditModalVisible(false); setEditingHistory(null); loadHistory(); }}
        historyData={editingHistory}
        currentUser={currentUser}
      />
    </div>
  );
};

// ==================== 系统设置（Google 同步） ====================
// 单个 sheet 行：用独立组件承载，避免在 map 回调里调 hook
const GoogleSheetRow = ({ sheetKey, initialEnabled, initialTitle }) => {
  const [checked, setChecked] = useState(initialEnabled);
  return (
    <Card size="small" style={{ marginBottom: 8 }}>
      <Row gutter={16} align="middle">
        <Col span={6}>
          <Form.Item name={`sheet_${sheetKey}`} valuePropName="checked" style={{ marginBottom: 0 }} initialValue={initialEnabled}>
            <Checkbox onChange={e => setChecked(e.target.checked)}>{initialTitle}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item name={`title_${sheetKey}`} style={{ marginBottom: 0 }} initialValue={initialTitle}>
            <Input placeholder="工作表名" disabled={!checked} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>key: {sheetKey}</Text>
        </Col>
      </Row>
    </Card>
  );
};

const GoogleSyncConfig = ({ currentUser }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/google-sync-config?username=${encodeURIComponent(currentUser.username)}`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        form.setFieldsValue({
          enabled: data.enabled,
          spreadsheetId: data.spreadsheetId,
          intervalHours: data.intervalHours,
        });
      } else {
        messageApi.error('加载同步配置失败');
      }
    } catch (e) {
      messageApi.error('加载同步配置失败');
    }
    setLoading(false);
  }, [currentUser, form, messageApi]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      // 从表单值 + config.sheets 组装 sheets 开关
      const sheets = {};
      for (const key of Object.keys(config?.sheets || {})) {
        sheets[key] = {
          enabled: values[`sheet_${key}`],
          title: values[`title_${key}`] || config.sheets[key].title,
        };
      }
      const res = await fetch(`${API_BASE_URL}/api/admin/google-sync-config?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: values.enabled,
          spreadsheetId: values.spreadsheetId,
          intervalHours: values.intervalHours,
          sheets,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        messageApi.success('同步配置已保存');
        setConfig(data.config);
      } else {
        messageApi.error(data.detail || '保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败');
    }
    setSaving(false);
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/google-sync?username=${encodeURIComponent(currentUser.username)}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.enabled) {
        messageApi.success('同步已执行，查看下方结果');
      } else {
        messageApi.warning(data.message || data.error || '同步未执行');
      }
    } catch (e) {
      messageApi.error('同步失败');
    }
    setSyncing(false);
  };

  if (!config) {
    return <Card loading={loading}><div style={{ height: 200 }} /></Card>;
  }

  const sheetKeys = Object.keys(config.sheets);

  return (
    <div>
      {contextHolder}
      <Card
        title={<><CloudOutlined /> Google Sheets 同步配置</>}
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={handleSyncNow} loading={syncing}>立即同步一次</Button>
            <Button icon={<ReloadOutlined />} onClick={loadConfig}>刷新</Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="私钥 google_service_account.json 需手工放到服务器 backend 目录，不在此处配置。这里只配置启用开关、表格 ID、同步频率和要同步的数据表。"
        />
        <Form form={form} layout="vertical" onFinish={handleSave} initialValues={{ enabled: false, intervalHours: 4 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="enabled" label="启用定时同步" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="intervalHours" label="同步频率（小时）" rules={[{ required: true, message: '请输入频率' }]}>
                <InputNumber min={1} max={168} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="spreadsheetId" label="表格 ID（spreadsheet_id）" rules={[{ required: true, message: '请输入表格 ID' }]}
            extra="从 Google Sheet 的 URL 里复制 /d/ 和 /edit 之间的那串">
            <Input placeholder="1AbCdEf..." />
          </Form.Item>

          <Divider orientation="left">要同步的数据表</Divider>
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="取消勾选的数据表在下次同步时会被跳过（不会被删除，只是不再更新）。" />
          {sheetKeys.map(key => (
            <GoogleSheetRow key={key} sheetKey={key} initialEnabled={config.sheets[key].enabled} initialTitle={config.sheets[key].title} />
          ))}

          <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>保存配置</Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

// ==================== 管理页面（仅管理员可见）====================
const AdminPage = ({ currentUser, onConfigChange }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [activeTab, setActiveTab] = useState('users');

  const tabItems = [
    { key: 'users', label: <><UserOutlined /> 用户管理</>, children: <UserManagement currentUser={currentUser} /> },
    { key: 'boss', label: <><SettingOutlined /> Boss 状态配置</>, children: <BossConfig onConfigChange={onConfigChange} /> },
    { key: 'loot', label: <><GiftOutlined /> 掉落物品配置</>, children: <LootConfig onConfigChange={onConfigChange} /> },
    { key: 'admins', label: <><CrownOutlined /> 管理员</>, children: <AdminConfig currentUser={currentUser} /> },
    { key: 'history', label: <><HistoryOutlined /> 历史记录</>, children: <HistoryManagement currentUser={currentUser} /> },
    { key: 'system', label: <><CloudOutlined /> 系统设置</>, children: <GoogleSyncConfig currentUser={currentUser} /> },
  ];

  return (
    <div style={{ padding: isMobile ? '0 8px' : '0 24px' }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size={isMobile ? 'small' : 'default'} />
    </div>
  );
};

export { BossConfig, LootConfig, AdminConfig, AdminPage, HistoryManagement, UserManagement };
